/**
 * Shared chrome for the logged-in dashboard previews on the landing: a labelled
 * frame (like the design canvas's "1b · LOGGED-IN HOME" tab), the dark iAdvocate
 * rail (with the account chip the live rail pins at its foot), and the
 * parchment top bar — search with ⌘K, theme toggle, bell, avatar — the live
 * header's furniture. The caller supplies the nav items, the search
 * placeholder, an optional primary-action label and the canvas body.
 *
 * MARKETING-STATIC preview — non-interactive by design (aria-hidden), so the
 * fake nav/search never traps keyboard focus or implies a working control.
 */
import type { ReactNode } from 'react';
import ColumnMark from '../../../chambers/ColumnMark';
import type { NavItem, RailUser } from './previewData';

interface PreviewFrameProps {
  label: string;
  railBrandSub?: string;
  nav: NavItem[];
  railUser?: RailUser;
  searchPlaceholder: string;
  /** Rendered beside the search bar when given; the live criminal header has none. */
  actionLabel?: string;
  children: ReactNode;
}

export default function PreviewFrame({
  label,
  railBrandSub,
  nav,
  railUser,
  searchPlaceholder,
  actionLabel,
  children,
}: PreviewFrameProps) {
  return (
    <div className="relative" aria-hidden="true">
      {/* canvas tab label */}
      <span className="absolute -top-3 left-0 z-10 font-plex text-[11px] font-semibold tracking-[0.08em] bg-brass text-chamber-ink px-3 py-0.5">
        {label}
      </span>

      <div className="grid grid-cols-1 sm:grid-cols-[200px_minmax(0,1fr)] bg-parchment border border-chamber-border overflow-hidden shadow-[0_30px_80px_rgba(0,0,0,0.45)]">
        {/* rail */}
        <div className="hidden sm:flex flex-col gap-0.5 bg-chamber-rail px-4 py-6">
          <div className="flex items-center gap-2 px-1 pb-4">
            <ColumnMark className="h-5 w-auto text-brass" />
            <div>
              <div className="font-display text-[13px] tracking-[0.12em] text-parchment">
                ADVOCATE
              </div>
              {railBrandSub && (
                <div className="font-plex text-[7px] tracking-[0.28em] text-brass mt-0.5">
                  {railBrandSub}
                </div>
              )}
            </div>
          </div>
          {nav.map((item, i) =>
            item.heading ? (
              <div
                key={`h-${i}`}
                className="font-plex text-[8px] tracking-[0.3em] text-chamber-muted-faint px-2 pt-3 pb-1.5"
              >
                {item.label}
              </div>
            ) : (
              <div
                key={item.label}
                className={`flex items-center gap-2.5 px-2.5 py-2 text-[11px] font-medium ${
                  item.active
                    ? 'bg-brass text-chamber-ink'
                    : 'text-chamber-muted'
                }`}
              >
                <span className="w-3 text-center text-[10px]">{item.icon}</span>
                <span className="flex-1 truncate">{item.label}</span>
                {item.active && <span className="text-[10px]">›</span>}
                {item.soon && (
                  <span className="font-plex text-[6.5px] tracking-[0.14em] border border-brass/50 text-brass px-1 py-0.5">
                    SOON
                  </span>
                )}
              </div>
            ),
          )}
          {railUser && (
            <div className="mt-auto pt-4">
              <div className="flex items-center gap-2 border border-brass-hairline-faint bg-chamber-panel px-2.5 py-2">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brass font-display text-[11px] text-chamber-ink">
                  {railUser.initial}
                </span>
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="truncate text-[11px] font-medium text-parchment">
                      {railUser.name}
                    </span>
                    <span className="font-plex text-[7px] tracking-[0.12em] bg-brass text-chamber-ink px-1 py-px">
                      {railUser.plan}
                    </span>
                  </div>
                  <div className="font-plex text-[7px] tracking-[0.2em] text-chamber-muted-faint mt-0.5">
                    {railUser.practice}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* canvas */}
        <div className="min-w-0 p-4 sm:p-5 lg:p-6 flex flex-col gap-4">
          {/* top bar: search + header furniture */}
          <div className="flex items-center gap-3">
            <div className="flex-1 flex items-center gap-2 border border-parchment-border-strong bg-parchment-card px-3 py-2 text-[11px] text-parchment-muted-faint">
              <span>⌕</span>
              <span className="truncate">{searchPlaceholder}</span>
              <span className="ml-auto font-plex text-[9px] border border-parchment-border-strong px-1 py-0.5">
                ⌘K
              </span>
            </div>
            {actionLabel ? (
              <div className="hidden md:block border border-brass text-brass-ink px-3 py-2 font-plex text-[10px] font-semibold tracking-[0.06em]">
                {actionLabel}
              </div>
            ) : (
              <div className="hidden md:flex items-center gap-3 text-chamber-panel text-[12px]">
                <span>☾</span>
                <span>♫</span>
                {railUser && (
                  <span className="flex h-6 w-6 items-center justify-center rounded-full border border-brass bg-brass/20 font-display text-[11px] text-chamber-panel">
                    {railUser.initial}
                  </span>
                )}
              </div>
            )}
          </div>

          {children}
        </div>
      </div>
    </div>
  );
}
